import { Share2 } from "lucide-react";

import { toast } from "~/components/ui/use-toast";
import { cn } from "~/lib/utils";

type ShareCourseButtonProps = {
  /** Slug del curso. Es lo que arma la url PÚBLICA, que es la que se comparte. */
  slug: string | null | undefined;
  /** Título del curso, para el texto que ofrece la hoja nativa de compartir. */
  title: string;
  className?: string;
};

/**
 * Compartir un curso.
 *
 * SIEMPRE comparte la ficha pública (`/courses/:slug`), incluso desde la pantalla interna, y
 * eso no es un detalle: la interna se sirve con `noindex` y sin OG por curso —lo dice su
 * propio `meta`—, así que un link a ella llega a WhatsApp como una tarjeta en blanco. La
 * pública trae título, descripción e imagen, que es lo que hace que el mensaje se vea como
 * algo que vale la pena abrir. Y funciona sin sesión, que es el punto de compartir.
 *
 * En teléfono abre la hoja NATIVA del sistema: ahí están WhatsApp, Telegram y el mail que la
 * persona ya usa, sin que nosotros tengamos que mantener una lista de botones por red. En
 * escritorio, donde esa hoja casi no existe, copia el link y avisa.
 *
 * `navigator` se toca dentro del click y nunca en el render: leerlo antes rompe el servidor,
 * que no tiene navegador (ver las trampas de hidratación en CLAUDE.md).
 */
export const ShareCourseButton = ({ slug, title, className }: ShareCourseButtonProps) => {
  // Sin slug no hay url pública que compartir. Es un dato del curso, no del estado: si falta,
  // el botón directamente no existe en vez de ofrecer algo que va a fallar.
  if (!slug) return null;

  const compartir = async () => {
    const url = `${window.location.origin}/courses/${slug}`;

    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        // Cancelar la hoja de compartir NO es un error: el alumno cambió de idea. Cualquier
        // otro fallo cae al copiado, que es mejor que dejarlo sin nada.
        if (error instanceof Error && error.name === "AbortError") return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      toast({ description: "Link copiado. Ya lo podés pegar donde quieras." });
    } catch {
      // El portapapeles pide contexto seguro y permiso; si el navegador lo niega, se dice.
      toast({ variant: "destructive", description: "No pudimos copiar el link." });
    }
  };

  return (
    <button
      type="button"
      onClick={compartir}
      title="Compartir este curso"
      className={cn(
        "inline-flex items-center gap-1.5 rounded-[8px] px-2.5 py-1.5 text-[13.5px] font-semibold text-landing-muted transition-colors hover:bg-landing-surface hover:text-landing-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-landing-red/40",
        className,
      )}
    >
      <Share2 className="size-3.5" aria-hidden="true" />
      Compartir
    </button>
  );
};
